import { ChangeEvent, ReactNode, useId } from 'react';
import { InputAdornment, TextField as MuiTextField, TextFieldProps } from '@mui/material';
import { InputLabelContainer } from '@components/container/InputLabelContainer';
import { AdditionalInfoLinkProps } from '@components/container/AdditionalInfoLink';
import { staticInputLabelStyles, textFieldStyles } from './TextField.styles';

// Input types where native browser spell checking is unhelpful or undesirable
// (credentials, structured/technical values, search). Free-text inputs default
// to spellcheck enabled; these opt out unless a caller explicitly overrides.
const SPELLCHECK_DISABLED_TYPES = new Set([
  'password',
  'email',
  'url',
  'tel',
  'number',
  'search',
  'date',
  'datetime-local',
  'time',
  'month',
  'week',
]);

export type TextFieldExtendedProps = TextFieldProps & {
  inputLabel?: ReactNode;
  inputLabelIcon?: ReactNode;
  tooltipTitle?: ReactNode;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  externalIcon?: ReactNode;
  onPressEndIcon?: () => void;
  helperLabel?: string | ReactNode;
  otherInputProps?: {};
  onChangeInput?: (eventValue: string) => void;
  handleOnPattern?: (str: string) => string;
  isDatePicker?: boolean;
  additionalInfoLink?: AdditionalInfoLinkProps;
  deprecated?: boolean | null;
  requiredBeforeBind?: boolean;
  config?: {
    allowHelperLabelWrap?: boolean;
  };
};

export function TextField({
  id,
  inputLabel,
  inputLabelIcon,
  tooltipTitle,
  startIcon,
  endIcon,
  externalIcon,
  onPressEndIcon,
  helperLabel,
  defaultValue,
  variant = 'outlined',
  error,
  disabled,
  value,
  sx,
  otherInputProps,
  handleOnPattern,
  onChangeInput,
  isDatePicker: _isDatePicker,
  required,
  additionalInfoLink,
  slotProps,
  deprecated,
  requiredBeforeBind,
  config,
  ...other
}: TextFieldExtendedProps) {
  const hasIcon = startIcon || endIcon;

  const renderIconColor = (): string => {
    let iconColor = 'text.main';
    if (disabled) {
      iconColor = 'text.disabled';
    } else if (value === undefined || value === '') {
      iconColor = 'text.subdued';
    }
    return iconColor;
  };

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (typeof handleOnPattern === 'function') {
      onChangeInput?.(handleOnPattern(event.target.value));
      return;
    }
    onChangeInput?.(event.target.value);
  };

  const withIconStyles = {
    '& .MuiOutlinedInput-root':
      other?.size === 'small'
        ? {
            padding: hasIcon ? '4px 16px 4px 6px' : '4px 12px',
            input: {
              padding: hasIcon ? '0px 8px 0px 4px' : 0,
            },
          }
        : {
            padding: hasIcon ? '8px 12px 8px 8px' : '8px 16px',
            input: {
              padding: hasIcon ? '0px 16px 0px 8px' : 0,
            },
          },
  } as const;

  const generatedId = useId();
  const fieldId = id ?? other.name ?? generatedId;

  const {
    autoComplete,
    autoFocus,
    color,
    fullWidth,
    helperText,
    inputRef,
    label,
    margin,
    multiline,
    placeholder,
    rows,
    maxRows,
    minRows,
    select,
    size,
    type,
    spellCheck,
    onChange,
    onBlur,
    onFocus,
    onClick,
    onMouseDown,
    InputLabelProps,
    FormHelperTextProps,
    inputProps,
    InputProps,
    ...unknownOther
  } = other;

  // DUE to the old 'input props/broken out now existing slotProps' and us using endIcon and startIcon and otherInputProps...
  // we need to ensure that we are not overwriting the input props with the slotProps
  const slotPropsToUse: any = slotProps ?? {};

  if (otherInputProps || InputProps) {
    slotPropsToUse.input = {
      ...InputProps,
      ...slotPropsToUse.input,
      ...otherInputProps,
    };
  }

  if (inputProps) {
    slotPropsToUse.htmlInput = {
      ...inputProps,
      ...slotPropsToUse.htmlInput,
    };
  }

  // Enable native browser spell checking for multiline free-text inputs only.
  // An explicit `spellCheck` prop, or a value set via slotProps.htmlInput, always wins.
  const resolvedSpellCheck = spellCheck ?? (Boolean(multiline) && !SPELLCHECK_DISABLED_TYPES.has(type ?? 'text'));
  slotPropsToUse.htmlInput = {
    spellCheck: resolvedSpellCheck,
    ...slotPropsToUse.htmlInput,
  };

  if (endIcon) {
    slotPropsToUse.input = {
      ...slotPropsToUse.input,
      endAdornment: (
        <InputAdornment
          disablePointerEvents={disabled}
          variant='standard'
          onClick={onPressEndIcon}
          position='end'
          sx={(theme) => ({
            color: renderIconColor(),
            ...theme.icons.xs,
          })}
        >
          {endIcon}
        </InputAdornment>
      ),
    };
  }

  if (startIcon) {
    slotPropsToUse.input = {
      ...slotPropsToUse.input,
      startAdornment: (
        <InputAdornment
          position='start'
          sx={(theme) => ({
            height: 0, // the icon displays fine, this prevents the box from growing due to the icon and being taller than our set textbox height
            color: renderIconColor(),
            svg: {
              ...theme.icons.xs,
            },
          })}
        >
          {startIcon}
        </InputAdornment>
      ),
    };
  }

  if (FormHelperTextProps) {
    slotPropsToUse.formHelperText = {
      ...slotPropsToUse.formHelperText,
      ...FormHelperTextProps,
    };
  }

  return (
    <InputLabelContainer
      inputLabelProps={{
        children: inputLabel,
        endIcon: inputLabelIcon,
        tooltipTitle,
        error,
        disabled,
        required,
        deprecated,
        requiredBeforeBind,
        htmlFor: fieldId,
        sx: staticInputLabelStyles,
        ...InputLabelProps,
      }}
      helperLabelProps={{
        children: helperLabel,
        error,
        disabled,
      }}
      allowHelperLabelWrap={config?.allowHelperLabelWrap}
      additionalInfoLink={additionalInfoLink}
    >
      <MuiTextField
        name={fieldId}
        id={fieldId}
        // NOTE 5/2, using defaultValue + value not allowed, this is to only use this if a value is given.
        {...(!defaultValue && defaultValue !== '' ? { value: value ?? '' } : { defaultValue })}
        variant={variant}
        error={error}
        disabled={disabled}
        required={required}
        onChange={onChange ? onChange : handleOnChange}
        onBlur={onBlur}
        onFocus={onFocus}
        onClick={onClick}
        onMouseDown={onMouseDown}
        sx={[withIconStyles, textFieldStyles, ...(Array.isArray(sx) ? sx : [sx])]}
        slotProps={slotPropsToUse}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        color={color}
        fullWidth={fullWidth}
        helperText={helperText}
        inputRef={inputRef}
        label={label}
        margin={margin}
        multiline={multiline}
        placeholder={placeholder}
        rows={rows}
        maxRows={maxRows}
        minRows={minRows}
        select={select}
        size={size}
        type={type}
        {...unknownOther}
      />
      {externalIcon}
    </InputLabelContainer>
  );
}
